import {
  ContactRole,
  ContactRolesResponse,
  ContactRoleResponse,
  CreateContactRoleRequest,
  UpdateContactRoleRequest
} from '../types/contact'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class ContactRoleService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all contact roles
   * @param activeOnly - Optional filter to return only active roles
   */
  async getContactRoles(activeOnly?: boolean): Promise<ContactRole[]> {
    const url = new URL(`${this.baseURL}/contact-roles`)

    if (activeOnly !== undefined) {
      url.searchParams.append('is_active', String(activeOnly))
    }

    const timeoutMs = 30000 // 30 second timeout

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ContactRolesResponse>(response)

    return responseData.data
  }

  /**
   * Get a single contact role by ID
   * @param id - Contact role UUID
   */
  async getContactRole(id: string): Promise<ContactRole> {
    const url = `${this.baseURL}/contact-roles/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<ContactRoleResponse>(response)

    return data.data
  }

  /**
   * Create a new contact role
   * @param roleData - Contact role data
   */
  async createContactRole(roleData: CreateContactRoleRequest): Promise<ContactRole> {
    const url = `${this.baseURL}/contact-roles`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(roleData)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<ContactRoleResponse>(response)

    return data.data
  }

  /**
   * Update an existing contact role
   * @param id - Contact role UUID
   * @param updates - Partial contact role data to update
   */
  async updateContactRole(id: string, updates: UpdateContactRoleRequest): Promise<ContactRole> {
    const url = `${this.baseURL}/contact-roles/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(updates)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<ContactRoleResponse>(response)

    return data.data
  }

  /**
   * Delete a contact role (soft delete by setting is_active to false)
   * @param id - Contact role UUID
   */
  async deleteContactRole(id: string): Promise<void> {
    const url = `${this.baseURL}/contact-roles/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    await this.handleResponse<{ success: boolean; message: string }>(response)
  }
}

export const contactRoleService = new ContactRoleService()
